/** @jsx h */
import { $ } from '@3pc/dom';
import { labels, endpoints, searchType } from '../config';
import { Fragment } from 'preact';
import { useState, useRef, useEffect } from 'preact/hooks';
import { fetchSearchData, replaceUrl } from '../helpers/helpers';
import { searchStore } from '../store/store';
import EndpointsContainer from '../components/EndpointsContainer';
import FilterContainer from '../components/FilterContainer';
import Header from '../components/Header';
import Loading from '../components/Loading';
import MainInput from '../components/MainInput';
import ResultContainer from '../components/ResultContainer';
import Autosuggest from '../helpers/Autosuggest';
import { ClickRedirect } from '@3pc/click-redirect';

const GlobalSearch = ({
  initialSearchData,
  pluginNamespace,
  initialEndpoint,
}) => {
  // set default states
  const [searchData, setSearchData] = useState(initialSearchData);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isReset, setIsReset] = useState(false);
  const [resultIsEmpty, setResultIsEmpty] = useState(
    searchData.allResultCount === 0
  );
  const [inputQuery, setInputQuery] = useState(searchData.arguments);
  const [resultList, setResultList] = useState(
    searchData.documents.list.results
  );
  const [currentEndpoint, setCurrentEndpoint] = useState({});
  const [previousEndpoint, setPreviousEndpoint] = useState(initialEndpoint.url);
  const [autosuggestUrl, setAutosuggestUrl] = useState(
    initialEndpoint.suggestUrl
  );
  const [autoSuggestClass, setAutoSuggestClass] = useState(null);
  const formEl = useRef(null);
  const isMainSearch = searchType === 'all';

  // get setSearchJson function from the store
  const setSearchJson = searchStore(state => state.setSearchJson);

  function resetFilters(formData) {
    // if its a new quey, we need to reset all previous facets
    for (const key of formData.keys()) {
      // clear all filter except input query
      if (key !== `${pluginNamespace}[q]`) formData.delete(key);
    }

    formData.delete(`${pluginNamespace}[filter][]`);
    setIsReset(true);
    const zipInput = document.querySelector('#zip-code');
    if (zipInput) zipInput.value = '';
  }

  function updateEndpoint(url) {
    const newEndpointObj = endpoints.filter(
      endpoint => endpoint.url === url
    )[0];
    triggerSearch(false, inputQuery, url);
    setCurrentEndpoint(newEndpointObj);
    setAutosuggestUrl(newEndpointObj.suggestUrl);
  }

  function setPopularSearchTermAsInputQuery(query) {
    setInputQuery(query);
    $('[data-main-input]').value = query;
    triggerSearch(false, query);
  }

  function handleSubmit(e) {
    e && e.preventDefault();
    triggerSearch();
  }

  function handleLoadMore() {
    triggerSearch(true);
  }

  function handleReset() {
    formEl.current.reset();
    setCurrentEndpoint({});
    setIsReset(true);
    setAutosuggestUrl(initialEndpoint.suggestUrl);
    triggerSearch(false, '', initialEndpoint.url);
  }

  function triggerSearch(
    isLoadMore = false,
    query = '',
    url = currentEndpoint.url || initialEndpoint.url
  ) {
    const formData = new FormData(formEl.current);

    // cancel on-going autosuggest call
    if (autoSuggestClass) {
      autoSuggestClass.cancelAjax(true);
    }

    // update query, if one was passed into this function
    if (query) formData.set(`${pluginNamespace}[q]`, query);
    const formQuery = formData.get(`${pluginNamespace}[q]`);

    // check if user is searching with a new query
    if (formQuery && inputQuery) {
      const isNewQuery = formQuery !== inputQuery;
      if (isNewQuery) resetFilters(formData);
    } else if ((formQuery && !inputQuery) || (!formQuery && inputQuery)) {
      resetFilters(formData);
    }

    setInputQuery(formQuery);

    const newEndpointSelected =
      previousEndpoint.length && url !== previousEndpoint;
    if (newEndpointSelected) resetFilters(formData);

    setPreviousEndpoint(url);

    if (formData.get(`${pluginNamespace}[q]`) === '') {
      formData.delete(`${pluginNamespace}[q]`);
    }
    if (formData.get(`${pluginNamespace}[sort]`) === '') {
      formData.delete(`${pluginNamespace}[sort]`);
    }
    if (!isLoadMore && formData.get(`${pluginNamespace}[page]`) === '') {
      formData.delete(`${pluginNamespace}[page]`);
    }
    if (
      newEndpointSelected &&
      formData.get(`${pluginNamespace}[page]`) === ''
    ) {
      formData.delete(`${pluginNamespace}[page]`);
    }

    getSearchData(formData, isLoadMore, url);
  }

  function getSearchData(formData, isLoadMore = false, url) {
    setIsLoading(true);
    setIsError(false);
    fetchSearchData(url, formData)
      .then(({ data }) => {
        const resultData = data.content.colPos0[0].content.data;
        const hasNoResults = resultData.allResultCount === 0;
        const resultDataList = resultData.documents.list.results;

        // if there are no results, show no-result text
        if (hasNoResults) {
          setResultIsEmpty(true);
          setResultList([]);
          setSearchJson(resultData);
          setSearchData(resultData);
        } else {
          setResultIsEmpty(false);
          setSearchData(resultData);

          if (isLoadMore) {
            // append next item into array, when is loadmore (cause we dont want to replace the prev items)
            setResultList([...resultList, ...resultDataList]);

            /*
             * onLoadMore, we dont want to replace the whole searchJson (from the store)
             * We only need to update the pagination number and the result count
             * and append the new incoming result list to the old one
             */
            const searchJson = searchStore.getState().searchJson;
            const searchJsonCount = searchJson.documents.list.count;

            // append new result to the old result
            searchJson.documents.list.results = [
              ...resultList,
              ...resultDataList,
            ];

            // update searchJson count
            searchJson.documents.list.count =
              searchJsonCount + resultData.documents.list.count;

            // update current pagination
            searchJson.documents.pagination.current =
              resultData.documents.pagination.current;

            // save it in our searchStore (needed for popstate later)
            setSearchJson(searchJson);
          } else {
            // replace the whole list
            setResultList(resultDataList);

            // save response json in our searchStore (needed for popstate later)
            setSearchJson(resultData);
          }
        }

        // add search params to the url
        replaceUrl(
          process.env.NODE_ENV === 'production' ? url : '/api/v1/main',
          resultData.currentSearch,
          formData,
          pluginNamespace
        );

        setIsReset(false);
        setIsLoading(false);
      })
      .catch(error => {
        console.error('Search Error:', error);
        setIsLoading(false);
        setIsError(true);
      });
  }

  // init autosuggest
  useEffect(() => {
    if (autosuggestUrl) {
      const form = formEl.current;
      const searchInput = $('[data-main-input]', form);

      if (form && searchInput) {
        const autoSuggestObj = new Autosuggest({
          // suggest endpoint
          serviceUrl: autosuggestUrl,
          // input which triggers solr
          input: searchInput,
          // form element which contains the input
          form,
          // [optional] min length of text until we fire the ajax-request (0 is default)
          textMinLength: 2,
          // [optional] set true, if you want to enable typeahead
          typeahead: false,
          // [optional] prevent AutoSuggest from submitting the form (e.g. if you want to trigger ajax instead) (default is false)
          preventSubmit: true,
          // [optional] allow empty submit even if input is empty (default is false)
          allowEmptySubmit: true,
          // [optional] submit form if user clicks on a suggestion button (default is false)
          submitOnSuggestionClick: false,
          onSelecting: () => triggerSearch(),
        });
        autoSuggestObj.init();

        setAutoSuggestClass(autoSuggestObj);
      }
    }
  }, [autosuggestUrl]);

  useEffect(() => {
    // init click redirect on every resultlist update
    const clickRedirectList = ClickRedirect.InitViaDom();
    clickRedirectList.forEach(clickRedirect => clickRedirect.activate());
  }, [resultList]);

  return (
    <Fragment>
      {/* show loading animation, while fetching */}
      {isLoading && <Loading />}
      <form action="#" onSubmit={handleSubmit} ref={formEl}>
        {isMainSearch && endpoints.length > 1 && (
          <EndpointsContainer
            currentEndpoint={
              currentEndpoint.url ? currentEndpoint.url : initialEndpoint.url
            }
            initialEndpoint={initialEndpoint.url}
            updateEndpoint={updateEndpoint}
          />
        )}

        {!isMainSearch && <Header />}

        {isMainSearch ? (
          <div className="search__container--medium">
            <MainInput
              query={inputQuery}
              pluginNamespace={pluginNamespace}
              layoutClass="searchbar--underlined"
            />
            <FilterContainer
              searchType={searchType}
              facets={searchData.facets}
              facetsActive={searchData.facetsActive.selected}
              triggerAjaxSubmit={handleSubmit}
              setPopularSearchTermAsInputQuery={
                setPopularSearchTermAsInputQuery
              }
              pluginNamespace={pluginNamespace}
              isMainSearch={isMainSearch}
              handleReset={handleReset}
              inputQuery={inputQuery}
              currentEndpoint={currentEndpoint}
              resultIsEmpty={resultIsEmpty}
            />
          </div>
        ) : (
          <div className="search__container--large search__container--green">
            <div className="search__container--small">
              {searchType !== 'network' ? (
                <MainInput
                  query={inputQuery}
                  pluginNamespace={pluginNamespace}
                  layoutClass=""
                />
              ) : (
                ''
              )}
              <FilterContainer
                facets={searchData.facets}
                facetsActive={searchData.facetsActive.selected}
                triggerAjaxSubmit={handleSubmit}
                setPopularSearchTermAsInputQuery={
                  setPopularSearchTermAsInputQuery
                }
                pluginNamespace={pluginNamespace}
                isMainSearch={isMainSearch}
                handleReset={handleReset}
                isReset={isReset}
                inputQuery={inputQuery}
                resultIsEmpty={resultIsEmpty}
              />
              {searchType === 'network' ? (
                <div className="search__submit">
                  <button type="submit" className="btn btn--secondary">
                    Suche abschicken
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="svg-search"
                      viewBox="0 0 24.03 24.08"
                      aria-hidden="true"
                    >
                      <path
                        fill="currentColor"
                        d="m21.72 23.59-7.82-7.8c-.65.53-1.4.95-2.28 1.25-.87.3-1.81.45-2.81.45-2.46 0-4.54-.85-6.25-2.56S0 11.16 0 8.75s.85-4.48 2.56-6.19S6.34 0 8.76 0s4.48.85 6.18 2.56c1.7 1.71 2.55 3.77 2.55 6.19 0 .97-.14 1.88-.43 2.74-.29.86-.72 1.67-1.3 2.42l7.89 7.82c.26.26.39.57.39.92s-.14.67-.42.94c-.27.27-.59.41-.96.41s-.69-.14-.94-.41ZM8.78 14.88c1.7 0 3.15-.6 4.33-1.8 1.18-1.2 1.77-2.64 1.77-4.34s-.59-3.14-1.77-4.34c-1.18-1.2-2.62-1.8-4.32-1.8s-3.18.6-4.38 1.8c-1.2 1.2-1.8 2.64-1.8 4.34s.6 3.14 1.8 4.34c1.2 1.2 2.66 1.8 4.38 1.8Z"
                      />
                    </svg>
                  </button>
                </div>
              ) : (
                ''
              )}
            </div>
          </div>
        )}
        <div className="search__results search__container--medium">
          {resultIsEmpty ? (
            <Fragment>
              <h2 className="sr-only">{labels.results.heading}</h2>
              <div
                className="search__result-state"
                aria-live="polite"
                aria-atomic="true"
              >
                <p>
                  <strong>{labels.results.nothingFound}</strong>
                </p>
              </div>
            </Fragment>
          ) : isError ? (
            <Fragment>
              <h2 className="sr-only">{labels.results.heading}</h2>
              <div
                className="search__result-state"
                aria-live="polite"
                aria-atomic="true"
              >
                <p>
                  <strong>{labels.search.failed}</strong>
                </p>
              </div>
            </Fragment>
          ) : (
            <ResultContainer
              searchType={searchType}
              facetsActive={searchData.facetsActive.selected}
              allResultCount={searchData.allResultCount}
              list={resultList}
              pagination={searchData.documents.pagination}
              loadMore={handleLoadMore}
              pluginNamespace={pluginNamespace}
              isMainSearch={isMainSearch}
              showPrio={searchData.showPrio}
            />
          )}
        </div>
      </form>
    </Fragment>
  );
};

export default GlobalSearch;
